import PropTypes from "prop-types";
// eslint-disable-next-line no-unused-vars
import * as React from "react";
import { memo, useCallback } from "react";
import { ficheProduitPropTypes } from "app/utils/propTypes.js";
import { QuotationPriceDetailContainer } from "app/pages/Booking/Quotation/PriceDetails/QuotationPriceDetailContainer.jsx";
import { PRODUCT_PAGE_CODE_STATUS } from "app/constants";
import ContactUs from "app/pages/.shared/ContactUs/ContactUs";
import CondensedBookWithConfidence from "app/pages/.shared/BookWithConfidence/CondensedBookWithConfidence";
import HolidaySummaryContainer from "app/pages/Booking/HolidaySummary/HolidaySummary/HolidaySummaryContainer";
import Button, {
	BUTTON_ICON_ALIGNMENTS,
	BUTTON_SIZES,
	BUTTON_VARIANTS,
} from "app/pages/.shared/form/Button";
import { FormattedMessage } from "react-intl";
import IconRight from "app/pages/.shared/static/icons/IconRight";
import { useLocation } from "react-router-dom";
import "./BookingAside.scss";
import PaymentDescriptionTermsContainer from "app/pages/Booking/Payment/PaymentDescriptionTerms/PaymentDescriptionTermsContainer";
import PayNowNote from "app/pages/.shared/PayNow/PayNowNote";
import AvisVerifiesContainer from "app/pages/.shared/AvisVerifies/AvisVerifiesContainer";
import MilesEarnedContainer from "app/pages/.shared/MilesEarned/MilesEarnedContainer";

const BookingAside = ({
	shop = "",
	ficheProduit = {},
	showPaymentDescriptionTerms,
	showQuotationCTAButton = false,
	showContactUs = false,
	showPriceDetails = false,
	showProductPicture = false,
	depositPercentage,
	isSubmitting = false,
	isQuoteLoading = false,
	isBookingEligibleForDeposit,
	isBookingDateEligibleForDeposit,
	enableDepositDisplay = false,
	availablePaymentMethods = [],
	availablePaymentTerms = [],
	total,
	hidePhoneContactUs,
	pricePerPerson,
}) => {
	const [, countryCode] = shop.split("-");
	const { pathname } = useLocation();
	const isPaymentPage = pathname.includes("/booking/payment");

	const handleCTAClick = useCallback(() => {
		const quotationCta = document.querySelector("#quotation-cta");
		if (quotationCta) {
			quotationCta.click();
		}
	}, []);

	const isCTALoading = isSubmitting || isQuoteLoading;

	const payNowNote = total > 0 && (
		<PayNowNote
			total={total}
			depositPercentage={depositPercentage}
			availablePaymentMethods={availablePaymentMethods}
			displayAmountSuffix={false}
		/>
	);

	let paymentDescriptionTermsContent;
	if (isBookingEligibleForDeposit) {
		paymentDescriptionTermsContent = payNowNote;
	} else {
		paymentDescriptionTermsContent = <PaymentDescriptionTermsContainer />;
	}
	const displayPaymentTermsContent =
		showPaymentDescriptionTerms &&
		availablePaymentTerms?.length > 0 &&
		(enableDepositDisplay ? isBookingDateEligibleForDeposit : true);

	return (
		<div className={`booking-aside${isPaymentPage ? " booking-aside--payment" : ""}`}>
			{ficheProduit.status === PRODUCT_PAGE_CODE_STATUS.SUCCESS ? (
				<div className="booking-aside__panel booking-aside__panel--my-booking">
					<HolidaySummaryContainer
						showBookingBanner
						showProductPicture={showProductPicture}
					/>
				</div>
			) : (
				<img
					key={ficheProduit.status}
					src="/static/icons/booking-aside-loader-skeleton.svg"
				/>
			)}

			<div style={{ position: "sticky", top: "0" }}>
				<div className="booking-aside__panel booking-aside__panel--price-detail">
					{showPriceDetails && !isQuoteLoading && <QuotationPriceDetailContainer />}

					{showQuotationCTAButton && (
						<>
							<Button
								variant={BUTTON_VARIANTS.FILLED}
								size={BUTTON_SIZES.LARGE}
								alignIcon={BUTTON_ICON_ALIGNMENTS.RIGHT}
								label={<FormattedMessage id="quotation.button.cta" />}
								icon={<IconRight />}
								onClick={handleCTAClick}
								disabled={isCTALoading}
								loading={isCTALoading}
							/>
						</>
					)}

					<MilesEarnedContainer
						price={pricePerPerson}
						suffixLabel={<FormattedMessage id="miles.earned.suffix" />}
						prefixMessageId={
							isPaymentPage
								? "miles.earned.payment.prefix"
								: "miles.earned.search.engine.prefix"
						}
					/>

					{displayPaymentTermsContent && <>{paymentDescriptionTermsContent}</>}
				</div>

				{countryCode === "GB" && (
					<div className="booking-aside__atol-abta">
						<CondensedBookWithConfidence />
					</div>
				)}

				<div className="booking-aside__avis-verifies">
					<AvisVerifiesContainer />
				</div>

				{showContactUs && (
					<div className="booking-aside__panel booking-aside__panel--contact">
						<ContactUs
							productReference={ficheProduit.id}
							hidePhone={hidePhoneContactUs}
						/>
					</div>
				)}
			</div>
		</div>
	);
};

BookingAside.propTypes = {
	isBookingEligibleForDeposit: PropTypes.bool,
	shop: PropTypes.string,
	ficheProduit: ficheProduitPropTypes,
	showPaymentDescriptionTerms: PropTypes.bool,
	showQuotationCTAButton: PropTypes.bool,
	showContactUs: PropTypes.bool,
	showPriceDetails: PropTypes.bool,
	showProductPicture: PropTypes.bool,
	depositPercentage: PropTypes.number,
	isSubmitting: PropTypes.bool,
	isQuoteLoading: PropTypes.bool,
	availablePaymentTerms: PropTypes.array,
	availablePaymentMethods: PropTypes.array,
	isBookingDateEligibleForDeposit: PropTypes.bool,
	enableDepositDisplay: PropTypes.bool,
	total: PropTypes.number,
	hidePhoneContactUs: PropTypes.bool,
	pricePerPerson: PropTypes.number,
};

export default memo(BookingAside);
